import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, message, Carousel } from 'antd';
import {
  Verify,
  Profile2User,
  Calendar,
  LanguageSquare,
  CalendarTick,
  Location,
  Gallery,
  Link2,
  LinkCircle,
  Building,
  Briefcase,
  Star,
  Element4,
  Add,
  Award,
  DocumentText,
  Edit2,
  Trash
} from 'iconsax-react';

import {
  useGetProfileInfoQuery,
  useGetBusinessAwardsQuery,
  useGetProfileBranchQuery,
  useGetProfileWarehouseQuery,
  useGetProfileCategoriesQuery,
  useGetCataloguesQuery,
} from '../../../store/services/sellerProfileCreationService';
import { useGetSlidersQuery, useAddSliderImagesMutation } from '../../../store/services/addSellerService';
import { reviewService } from '../../../store/services/Review';
import PreviewProfileItem from '../../SellerProfileCreation/PreviewProfileItem';
import EmptyImg from '../../../assets/images/empty.png';
import EmptyDarkImg from '../../../assets/images/empty-dark.png';
import ActivityLogsList from '@/components/AdminLogs/IndividualLog/ActivityLogsList';

interface ProfileContentProps {
  sellerName: string;
  sellerId?: string;
  canSetupMarketPlace?: boolean;
}

const ProfileContent: React.FC<ProfileContentProps> = ({
  sellerName,
  sellerId = '',
  canSetupMarketPlace = false,
}) => {
  const { t } = useTranslation();

  const { data: profileInfoData } = useGetProfileInfoQuery({ profileId: sellerId }, { skip: !sellerId });
  const { data: awardsData } = useGetBusinessAwardsQuery({ profileId: sellerId }, { skip: !sellerId });
  const { data: branchesData } = useGetProfileBranchQuery({ profileId: sellerId }, { skip: !sellerId });
  const { data: warehousesData } = useGetProfileWarehouseQuery({ profileId: sellerId }, { skip: !sellerId });
  const { data: categoriesData } = useGetProfileCategoriesQuery({ profileId: sellerId }, { skip: !sellerId });
  const { data: cataloguesData } = useGetCataloguesQuery({ profileId: sellerId }, { skip: !sellerId });
  const { data: slidersData, refetch: refetchSliders } = useGetSlidersQuery(sellerId, { skip: !sellerId });
  const { data: feedbacksData } = reviewService.useGetReceivedFeedbacksQuery(sellerId, { skip: !sellerId });
  const [addSliderImages, { isLoading: isUploadingSlider }] = useAddSliderImagesMutation();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleSliderImagesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const currentCount = slidersData?.payload?.length || 0;
      const maxAllowed = 5;

      if (currentCount + e.target.files.length > maxAllowed) {
        message.error(t('components.validations.maxFielsLength', { num: maxAllowed }));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const formData = new FormData();
      Array.from(e.target.files).forEach((file) => {
        formData.append('files', file);
      });
      try {
        await addSliderImages({ sellerProfileId: sellerId, data: formData }).unwrap();
        refetchSliders();
      } catch (err: any) {
        console.error('Failed to add slider images', err);
        const errorMessage = err?.data?.errors?.SLIDER || err?.data?.message || err?.message || 'Failed to upload images';
        message.error(errorMessage);
      }
    }
  };

  const profileInfo = profileInfoData?.payload;
  const awards = awardsData?.payload || [];
  const branches = branchesData?.payload || [];
  const warehouses = warehousesData?.payload || [];
  const categories = categoriesData?.payload || [];
  const catalogues = cataloguesData?.payload || [];
  const sliders = slidersData?.payload || [];
  const reviews = feedbacksData?.payload?.content || [];

  return (
    /* Outer Container: Transparent Background so gray page shows through */
    <div
      className="flex flex-col gap-6 h-full"
      data-testid="profile-content-container"
    >
      {/* Top Section Wrapper (Bio, Info, Sliders) */}
      <div className="card-main bg-white dark:bg-dark-surface p-6 rounded-xl flex flex-col gap-6 border border-[#E5E7EB] dark:border-dark-border shadow-sm" data-testid="profile-top-card">

        {/* Bio Header */}
        <div
          className="flex flex-col md:flex-row items-start gap-6 pb-2"
          data-testid="profile-bio-container"
        >
          {/* Dark Navy Blue Circle Logo */}
          <div
            className="w-32 h-32 rounded-full bg-transparent flex items-center justify-center shrink-0 border border-gray-200 dark:border-dark-border shadow-sm overflow-hidden"
            data-testid="profile-bio-image-wrapper"
          >
            {profileInfo?.photoUrl ? (
              <img src={profileInfo.photoUrl} alt="Company logo" className="w-full h-full object-cover" />
            ) : (
              <span className="text-[#111928] dark:text-white text-4xl font-serif tracking-widest" data-testid="profile-bio-image-initials">
                {profileInfo?.name?.slice(0, 3).toUpperCase() || 'GAP'}
              </span>
            )}
          </div>

          {/* Title, Verified Chip, and Bio Description */}
          <div className="flex-1 flex flex-col gap-3" data-testid="profile-bio-details">
            <div className="flex flex-wrap items-center gap-4" data-testid="profile-bio-header-row">
              <h1
                className="text-[28px] font-bold text-[#111928] dark:text-white tracking-tight"
                data-testid="profile-bio-name"
              >
                {profileInfo?.name || sellerName}
              </h1>
              {profileInfo?.isVerified && (
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F3F4F6] dark:bg-gray-800 rounded-lg text-[#16A34A] dark:text-[#4DA167] text-sm font-semibold border border-gray-100 dark:border-dark-border"
                  data-testid="profile-bio-verified-chip"
                >
                  <Verify size="18" variant="Bold" className="text-[#16A34A] dark:text-[#4DA167]" />
                  <span>{t('sellers.sellerProfile.details.verifiedSupplier')}</span>
                </div>
              )}
            </div>

            {profileInfo?.bio && (
              <p
                className="text-[14px] leading-relaxed text-[#090909]/80 dark:text-gray-300 font-normal max-w-4xl"
                data-testid="profile-bio-description-text"
              >
                {profileInfo.bio}
              </p>
            )}
          </div>
        </div>

        {/* Info Pills */}
        <div className="flex flex-wrap gap-3" data-testid="profile-meta-pills-container">
          {profileInfo?.employeesNumber && (
            <div
              className="flex items-center gap-2 px-3 py-2 bg-[#FAFAFA] dark:bg-gray-800 rounded-[8px]"
              data-testid="meta-pill-employees"
            >
              <Profile2User size="18" className="text-[#090909] dark:text-gray-200" variant="Bold" />
              <span className="text-[14px] font-normal text-[#090909] dark:text-gray-200">{profileInfo.employeesNumber}</span>
            </div>
          )}
          {profileInfo?.foundYear && (
            <div
              className="flex items-center gap-2 px-3 py-2 bg-[#FAFAFA] dark:bg-gray-800 rounded-[8px]"
              data-testid="meta-pill-founded"
            >
              <Briefcase size="18" className="text-[#090909] dark:text-gray-200" variant="Bold" />
              <span className="text-[14px] font-normal text-[#090909] dark:text-gray-200">{t('sellers.sellerProfile.details.founded', { year: profileInfo.foundYear })}</span>
            </div>
          )}
          {profileInfo?.languages && profileInfo.languages.length > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2 bg-[#FAFAFA] dark:bg-gray-800 rounded-[8px]"
              data-testid="meta-pill-languages"
            >
              <LanguageSquare size="18" className="text-[#090909] dark:text-gray-200" variant="Bold" />
              <span className="text-[14px] font-normal text-[#090909] dark:text-gray-200">
                {t('sellers.sellerProfile.details.languages', { langs: profileInfo.languages.map((l: any) => l.name).join(', ') })}
              </span>
            </div>
          )}
          {profileInfo?.createdAt && (
            <div
              className="flex items-center gap-2 px-3 py-2 bg-[#FAFAFA] dark:bg-gray-800 rounded-[8px]"
              data-testid="meta-pill-joined"
            >
              <Calendar size="18" className="text-[#090909] dark:text-gray-200" variant="Bold" />
              <span className="text-[14px] font-normal text-[#090909] dark:text-gray-200">{t('sellers.sellerProfile.details.joined', { date: profileInfo.createdAt })}</span>
            </div>
          )}
          {(profileInfo?.location || profileInfo?.localTime) && (
            <div
              className="flex items-center gap-2 px-3 py-2 bg-[#FAFAFA] dark:bg-gray-800 rounded-[8px]"
              data-testid="meta-pill-location"
            >
              <Location size="18" className="text-[#090909] dark:text-gray-200" variant="Bold" />
              <span className="text-[14px] font-normal text-[#090909] dark:text-gray-200">
                {profileInfo.location}
                {profileInfo.location && profileInfo.localTime && ' - '}
                {profileInfo.localTime}
              </span>
            </div>
          )}
        </div>

        {/* Website Link Box */}
        {profileInfo?.websiteLink && (
          <div data-testid="profile-website-link-container">
            <a
              href={profileInfo.websiteLink}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 bg-[#F3F6FD] dark:bg-blue-900/20 px-3 py-2 rounded-[8px] w-fit text-[#3C91E6] dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-400 font-normal text-[14px] transition-colors duration-150 ${profileInfo.websiteLink.endsWith('/profile-creation') ? 'opacity-60 pointer-events-none' : ''
                }`}
              data-testid="profile-website-link"
            >
              <LinkCircle size="16.67" className="text-[#3C91E6] dark:text-blue-300" variant="Bold" />
              <span data-testid="profile-website-link-text">{profileInfo.websiteLink}</span>
            </a>
          </div>
        )}

        {/* Advertise Slider — only after marketplace setup is complete */}
        {!canSetupMarketPlace && (sliders.length > 0 ? (
          <div className="flex flex-col gap-4 mt-2" data-testid="sliders-container">
            <div className="flex justify-between items-center">
              <h3 className="text-[16px] font-bold text-[#111928] dark:text-dark-text-primary" data-testid="sliders-title">
                {t('sellers.sellerProfile.details.advertiseSlider', 'Advertise slider images')}
              </h3>
              <button
                className="rounded-xl duration-300 ease-out border border-light-text-secondary flex items-center justify-center gap-2 px-6 py-2 bg-light-button-primary text-light-button-label-primary hover:bg-light-button-hover hover:text-light-button-label-hover hover:border-pink-500 text-xs font-semibold transition-all disabled:opacity-50"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingSlider}
              >
                <Add size="16" color="currentColor" variant="Bold" />
                <span>{isUploadingSlider ? t('uploading', 'Uploading...') : t('sellers.sellerProfile.details.addSlidersButton', 'Add Slider images')}</span>
              </button>
            </div>
            <Image.PreviewGroup>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {sliders.map((slider: any, idx: number) => (
                  <div
                    key={idx}
                    className="h-[200px] w-[200px] shrink-0 overflow-hidden rounded-lg bg-gray-100 shadow-sm"
                  >
                    <Image
                      src={slider.fileUrl || slider.blobFileUrl}
                      alt="Slider image"
                      rootClassName="!h-[200px] !w-[200px] block"
                      className="!h-[200px] !w-[200px] rounded-lg object-cover shadow-sm"
                      preview={{
                        mask: null,
                      }}
                    />
                  </div>
                ))}
              </div>
            </Image.PreviewGroup>
          </div>
        ) : (
          <div
            className="rounded-[12px] p-6 mt-2 flex flex-col gap-6 relative overflow-hidden"
            data-testid="advertise-slider-container"
          >
            {/* Custom dashed border overlay to match Figma 10,10 stroke-dasharray */}
            <div className="absolute inset-0 pointer-events-none rounded-[12px] dark:hidden" style={{ backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23ACACAC' stroke-width='2' stroke-dasharray='10, 10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")` }}></div>
            <div className="absolute inset-0 pointer-events-none rounded-[12px] hidden dark:block" style={{ backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%234B5563' stroke-width='2' stroke-dasharray='10, 10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")` }}></div>

            <div className="relative z-10 flex justify-between items-start" data-testid="advertise-slider-header">
              <div data-testid="advertise-slider-title-wrapper">
                <h3 className="text-[16px] font-semibold text-[#090909] dark:text-dark-text-primary" data-testid="advertise-slider-title">
                  {t('sellers.sellerProfile.details.advertiseSlider', 'Advertise slider images')}
                </h3>
                <p className="text-[14px] text-[#6B7280] dark:text-dark-text-secondary" data-testid="advertise-slider-desc">
                  {t('sellers.sellerProfile.details.advertiseSliderDesc', 'This slider will display in your marketplace profile')}
                </p>
              </div>
              <span className="text-[#9CA3AF] text-[14px]" data-testid="advertise-slider-count">
                {t('sellers.sellerProfile.details.sliderImageCount', { count: sliders.length, max: 5 })}
              </span>
            </div>

            <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center pb-4" data-testid="advertise-slider-empty-state">
              <div className="flex justify-center mb-6" data-testid="advertise-slider-icon-box">
                <img src={EmptyImg} alt="empty state" className="w-[200px] object-contain dark:hidden" />
                <img src={EmptyDarkImg} alt="empty state" className="w-[200px] object-contain hidden dark:block" />
              </div>
              <h3 className="text-[18px] font-bold text-[#090909] dark:text-dark-text-primary mb-2" data-testid="advertise-slider-empty-title">
                {t('sellers.sellerProfile.details.emptyTitle', 'Looks like there’s nothing here yet.')}
              </h3>
              <p className="text-[14px] text-[#6B7280] dark:text-dark-text-secondary max-w-md" data-testid="advertise-slider-empty-desc">
                {t('sellers.sellerProfile.details.emptyDesc', 'Ready to get started? Add your first information here to showcase what you offer and attract potential customers.')}
              </p>
              <button
                className="mt-6 rounded-xl duration-300 ease-out border border-light-text-secondary flex items-center justify-center gap-2 px-6 py-2 bg-light-button-primary text-light-button-label-primary hover:bg-light-button-hover hover:text-light-button-label-hover hover:border-pink-500 text-sm font-semibold transition-all shadow-sm disabled:opacity-50 z-20 cursor-pointer pointer-events-auto"
                data-testid="add-slider-images-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingSlider}
              >
                <Add size="20" color="currentColor" variant="Bold" />
                <span>{isUploadingSlider ? t('uploading', 'Uploading...') : t('sellers.sellerProfile.details.addSlidersButton', 'Add Slider images')}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Video introduction */}
      {profileInfo?.introductionVideoUrl && (
        <div className="card-main bg-white dark:bg-dark-surface p-6 rounded-xl flex flex-col gap-4 border border-[#E5E7EB] dark:border-dark-border shadow-sm" data-testid="video-intro-container">
          <h3 className="text-lg leading-7 text-light-text-primary dark:text-dark-text-primary font-bold break-all">
            {t('sellers.sellerProfile.details.videoIntroTitle', 'Video introduction')}
          </h3>
          <div className="w-full h-[300px] bg-gray-200 dark:bg-gray-800 rounded-xl overflow-hidden relative flex items-center justify-center">
            {profileInfo.introductionVideoUrl.includes('youtube.com') || profileInfo.introductionVideoUrl.includes('youtu.be') ? (
              <iframe
                src={
                  profileInfo.introductionVideoUrl.includes('youtube.com/watch?v=')
                    ? `https://www.youtube.com/embed/${profileInfo.introductionVideoUrl.split('v=')[1]?.split('&')[0]}`
                    : profileInfo.introductionVideoUrl.includes('youtu.be/')
                      ? `https://www.youtube.com/embed/${profileInfo.introductionVideoUrl.split('youtu.be/')[1]?.split('?')[0]}`
                      : profileInfo.introductionVideoUrl
                }
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full rounded-xl"
              />
            ) : (
              <video src={profileInfo.introductionVideoUrl} controls className="w-full h-full object-cover rounded-xl" />
            )}
          </div>
        </div>
      )}

      {/* Catalogues / Previous Work */}
      {catalogues.length > 0 && (
        <div className="card-main bg-white dark:bg-dark-surface p-6 rounded-xl flex flex-col gap-6 border border-[#E5E7EB] dark:border-dark-border shadow-sm" data-testid="catalogues-card-container">
          <div>
            <h3 className="text-[18px] leading-none text-[#090909] dark:text-dark-text-primary font-bold break-all mb-2">
              {t('components.catalogue')}
            </h3>
            <p className="text-[14px] text-[#6B7280] dark:text-dark-text-secondary">
              {t('components.categoryDesc')}
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {catalogues.map((cat: any, idx: number) => (
              <PreviewProfileItem
                key={cat.id || idx}
                title={cat.name || cat.projectName}
                description={cat.description}
                specs={cat.specs}
                catalogueMode
                attachments={cat.attachments?.map((att: any) => ({
                  ...att,
                  url: att.url || att.fileUrl || att.blobFileUrl,
                }))}
                dataTestId={`catalogue-item-${idx}`} isEditable={false} isDelete={false} />
            ))}
          </div>
        </div>
      )}

      {/* Industry */}
      {categories.length > 0 && categories.map((cat: any, cidx: number) => (
        <div key={cidx} className="card-main bg-white dark:bg-dark-surface p-6 rounded-xl flex flex-col gap-4 border border-[#E5E7EB] dark:border-dark-border shadow-sm" data-testid={`industry-card-${cidx}`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center shrink-0">
              <Element4 size="24" className="text-[#3C91E6]" variant="Bold" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-400 dark:text-gray-500 leading-none mb-1">{t('sellers.sellerProfile.details.industryTitle', 'Industry')}</span>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{cat.categoryName || cat.name}</h3>
            </div>
          </div>
          {cat.specialties && cat.specialties.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {cat.specialties.map((spec: any, idx: number) => (
                <div key={idx} className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium">
                  {spec.specialtyName || spec.name || spec}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Awards & certificates */}
      {awards.length > 0 && (
        <div className="card-main bg-white dark:bg-dark-surface p-6 rounded-xl flex flex-col gap-6 border border-[#E5E7EB] dark:border-dark-border shadow-sm" data-testid="awards-card-container">
          <h3 className="text-[16px] leading-none text-[#090909] dark:text-dark-text-primary font-bold break-all" data-testid="awards-card-title">
            {t('sellers.sellerProfile.details.awardsTitle')}
          </h3>
          <div className="flex flex-col" data-testid="awards-list">
            {awards.map((award: any, idx: number) => (
              <div key={award.id || idx} className="flex flex-col" data-testid={`award-item-${idx}`}>
                {/* Icon Box */}
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#FFF7F2] dark:bg-[#FFDCA8]/10 flex items-center justify-center mb-3" data-testid={`award-icon-${idx}`}>
                  <Award size="22" color="#F97316" variant="Bold" />
                </div>

                {/* Date / Year */}
                <span className="text-[13px] sm:text-[14px] text-[#9CA3AF] dark:text-gray-500 font-medium mb-1.5 leading-none" data-testid={`award-year-${idx}`}>
                  {`${t('components.award_year')} ${award.awardYear}`}
                </span>

                {/* Award Name */}
                <h4 className="text-[16px] sm:text-[18px] font-bold text-[#090909] dark:text-dark-text-primary mb-1.5 leading-tight" data-testid={`award-name-${idx}`}>
                  {award.awardName}
                </h4>

                {/* Description */}
                <p className="text-[13px] sm:text-[14px] text-[#4B5563] dark:text-dark-text-secondary leading-relaxed break-all" data-testid={`award-description-${idx}`}>
                  {award.description}
                </p>

                {idx !== awards.length - 1 && <div className="h-[1px] bg-[#E5E7EB] dark:bg-dark-border w-full mt-6 mb-6" data-testid={`award-divider-${idx}`}></div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Branches and Warehouses */}
      {(branches.length > 0 || warehouses.length > 0) && (
        <div className="card-main bg-white dark:bg-dark-surface p-6 rounded-xl flex flex-col gap-8 border border-[#E5E7EB] dark:border-dark-border shadow-sm" data-testid="locations-card-container">
          {branches.length > 0 && (
            <div className="flex flex-col gap-4" data-testid="branches-section">
              <h3 className="text-[18px] leading-none text-[#090909] dark:text-dark-text-primary font-bold break-all" data-testid="branches-title">
                {t('sellers.sellerProfile.details.branchesTitle')}
              </h3>
              <div className="flex flex-col" data-testid="branches-list">
                {branches.map((branch: any, idx: number) => (
                  <div key={branch.id || idx} className="flex flex-col" data-testid={`branch-item-${idx}`}>
                    <div className="flex gap-3 sm:gap-4 items-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#F4F5F6] dark:bg-gray-800 flex items-center justify-center shrink-0" data-testid={`branch-icon-${idx}`}>
                        <Building size="22" color="#4DA167" variant="Bold" />
                      </div>
                      <div className="flex flex-col gap-1 overflow-hidden w-full">
                        <h4 className="text-[15px] sm:text-[16px] font-semibold text-[#090909] dark:text-dark-text-primary truncate" data-testid={`branch-name-${idx}`}>
                          {branch.name}
                        </h4>
                        {branch.lat && branch.lang && (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${branch.lat},${branch.lang}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[13px] sm:text-[14px] text-[#3B82F6] dark:text-blue-400 hover:underline truncate"
                            data-testid={`branch-map-link-${idx}`}
                          >
                            {`https://www.google.com/maps/search/?api=1&query=${branch.lat},${branch.lang}`}
                          </a>
                        )}
                      </div>
                    </div>
                    {idx !== branches.length - 1 && <div className="h-[1px] bg-[#E5E7EB] dark:bg-dark-border w-full mt-4 mb-4" data-testid={`branch-divider-${idx}`}></div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {warehouses.length > 0 && (
            <div className="flex flex-col gap-4" data-testid="warehouses-section">
              <h3 className="text-[18px] leading-none text-[#090909] dark:text-dark-text-primary font-bold break-all" data-testid="warehouses-title">
                {t('sellers.sellerProfile.details.warehousesTitle')}
              </h3>
              <div className="flex flex-col" data-testid="warehouses-list">
                {warehouses.map((warehouse: any, idx: number) => (
                  <div key={warehouse.id || idx} className="flex flex-col" data-testid={`warehouse-item-${idx}`}>
                    <div className="flex gap-3 sm:gap-4 items-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#F4F5F6] dark:bg-gray-800 flex items-center justify-center shrink-0" data-testid={`warehouse-icon-${idx}`}>
                        <Building size="22" color="#4DA167" variant="Bold" />
                      </div>
                      <div className="flex flex-col gap-1 overflow-hidden w-full">
                        <h4 className="text-[15px] sm:text-[16px] font-semibold text-[#090909] dark:text-dark-text-primary truncate" data-testid={`warehouse-name-${idx}`}>
                          {warehouse.name}
                        </h4>
                        {warehouse.lat && warehouse.lang && (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${warehouse.lat},${warehouse.lang}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[13px] sm:text-[14px] text-[#3B82F6] dark:text-blue-400 hover:underline truncate"
                            data-testid={`warehouse-map-link-${idx}`}
                          >
                            {`https://www.google.com/maps/search/?api=1&query=${warehouse.lat},${warehouse.lang}`}
                          </a>
                        )}
                      </div>
                    </div>
                    {idx !== warehouses.length - 1 && <div className="h-[1px] bg-[#E5E7EB] dark:bg-dark-border w-full mt-4 mb-4" data-testid={`warehouse-divider-${idx}`}></div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <div className="card-main bg-white dark:bg-dark-surface p-6 rounded-xl flex flex-col gap-6 border border-[#E5E7EB] dark:border-dark-border shadow-sm" data-testid="reviews-card-container">
          <div className="flex items-center gap-2" data-testid="reviews-header">
            <Star size="20" className="text-gray-900 dark:text-white" variant="Bold" />
            <h3 className="text-lg leading-7 text-[#090909] dark:text-dark-text-primary font-bold break-all" data-testid="reviews-title">
              {t('sellers.sellerProfile.details.reviewsTitle')}
            </h3>
          </div>
          <div className="flex flex-col gap-8 pt-2" data-testid="reviews-list">
            {reviews.map((review: any, idx: number) => (
              <div key={review.id || idx} className={`flex flex-col gap-3 ${idx !== 0 ? 'pt-8 border-t border-gray-100 dark:border-gray-800' : ''}`} data-testid={`review-item-${idx}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden shrink-0 border border-green-500 p-0.5" data-testid={`review-avatar-${idx}`}>
                    <img src={review.reviewerImage || ''} alt="avatar" className="w-full h-full bg-gray-300 dark:bg-gray-600 rounded-full object-cover" />
                  </div>
                  <div className="flex flex-col">
                    <h4 className="text-sm font-bold text-[#090909] dark:text-white" data-testid={`review-reviewer-name-${idx}`}>{review.reviewerName || 'Anonymous'}</h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400" data-testid={`review-company-name-${idx}`}>{review.companyName || 'No company'}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed" data-testid={`review-comment-${idx}`}>
                  {review.comments}
                </p>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 mt-1">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500" data-testid={`review-date-${idx}`}>{t('sellers.reviews.submittedAt')} {review.createdAt}</span>
                  <div className="flex items-center gap-1" data-testid={`review-rating-${idx}`}>
                    <span className="text-xs font-bold text-[#090909] dark:text-white mr-1">({review.rating?.toFixed(1) || '0.0'})</span>
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size="12" className={i < (review.rating || 0) ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'} variant="Bold" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-main bg-white dark:bg-dark-surface p-6 rounded-xl flex flex-col gap-4 border border-[#E5E7EB] dark:border-dark-border shadow-sm" >  
        <ActivityLogsList id={sellerId} type="REGISTRATION" />
      </div>


      {/* Hidden file input for uploading sliders */}
      <input
        type="file"
        multiple
        accept="image/*"
        ref={fileInputRef}
        onChange={handleSliderImagesChange}
        className="hidden"
        data-testid="slider-file-input"
      />
    </div>
  );
};

export default ProfileContent;
